import React from "react";
import { HStack } from "web/react/emo/h-stack";
import { BaseText } from "web/react/emo/shared/components/base-text/base-text";
import { VStack } from "web/react/emo/v-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import environment from "web/script/modules/environment";
import * as styles from "./price.css";

const SizeToStyles = {
    sm: "callout",
    md: "subhead",
    xm: "title-3",
    lg: "title-2",
} as const;
export interface CommonProps {
    size?: keyof typeof SizeToStyles;
    fullPrice?: string | number;
    minPrice?: string | number;
    inStock: boolean;
    dataTestId?: string;
}

type ConditionalProps =
    | {
          minPrice: string | number;
          maxPrice: string | number;
          inStock: true;
          salePrice?: never;
          fullPrice?: never;
      }
    | {
          fullPrice: string | number;
          salePrice?: string | number | null;
          maxPrice?: never;
          minPrice?: never;
      };

export type PriceProps = CommonProps & ConditionalProps;

export function Price({
    fullPrice,
    minPrice,
    maxPrice,
    salePrice,
    inStock,
    size = "sm",
    dataTestId,
}: PriceProps): JSX.Element {
    let textStyle = SizeToStyles[size];

    const hideOriginalPrice = !inStock && salePrice;
    const { isMobileViewport } = useDomViewport();
    const isPDP = environment.get("pageType") === "product";

    function priceContent(): JSX.Element {
        return (
            <div data-testid="product-price" className={styles.priceWrapper}>
                {!hideOriginalPrice && (
                    <BaseText
                        textStyle={textStyle}
                        strikeThrough={salePrice || !inStock ? true : false}
                    >
                        {fullPrice || minPrice}
                    </BaseText>
                )}
                {salePrice && (
                    <BaseText
                        textStyle={textStyle}
                        strikeThrough={!inStock ? true : false}
                        color={"highlightBrand"}
                    >
                        {salePrice}
                    </BaseText>
                )}
                {minPrice && maxPrice && (
                    <BaseText textStyle={textStyle}>{` - ${maxPrice}`}</BaseText>
                )}
            </div>
        );
    }

    return isMobileViewport && !isPDP ? (
        <VStack align="start" spacing="xxxs" dataTestId={dataTestId}>
            {priceContent()}
        </VStack>
    ) : (
        <HStack align="start" spacing="xxxs" dataTestId={dataTestId}>
            {priceContent()}
        </HStack>
    );
}
